import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { gzMatch, gzOverviewQuery, gzRefresh, gzUnmatch, type GzEndpointRow, type GzOverview, type GzRefresh } from '../../api/securiteeGravityZone'
import { Spinner } from '../../ui/Spinner'
import { flowAge } from '../XdrFlowAlert'
import { GzStatusLabel } from './GzStatusLabel'
import './gravityzone.css'

/**
 * Securitee > GravityZone: protectia statiilor firmei, citita din GravityZone.
 * Cartonasele raspund la constatarea de audit "statii fara antivirus"; cele doua
 * liste spun golurile in ambele sensuri - in OD fara GZ (fara antivirus) si in
 * GZ fara OD (fara agentul nostru).
 */
/* Cat se verifica proba push-ului, ecranul reintreaba; proba are 3 minute (GravityZonePush::TEST_WAIT). */
const CHECK_POLL_MS = 3000
const CHECK_POLLS = 70

/* Dupa o citire pornita la deschidere, datele se recitesc cat lucreaza ph4. */
const SYNC_REFETCH_MS = 10000
const SYNC_WINDOW_MS = 120000

/**
 * Deschiderea ecranului, fara cron: citire daca datele au peste o ora, proba
 * daca push-ul tace de 2 ore; cat pleaca proba, se reintreaba.
 */
function useOpenRefresh(): GzRefresh | null {
  const [answer, setAnswer] = useState<GzRefresh | null>(null)
  useEffect(() => {
    let stopped = false
    let polls = 0
    let timer = 0
    const ask = () => {
      gzRefresh()
        .then((next) => {
          if (stopped) return
          setAnswer(next)
          if (next.push?.state === 'checking' && polls++ < CHECK_POLLS) timer = window.setTimeout(ask, CHECK_POLL_MS)
        })
        .catch(() => undefined)
    }
    ask()
    return () => {
      stopped = true
      window.clearTimeout(timer)
    }
  }, [])
  return answer
}

function PushStopped({ minutes }: { minutes: number }) {
  return (
    <div className="alert alert-danger gz-push-alert" role="alert">
      <strong>GravityZone Push stopped.</strong> No events from GravityZone for {flowAge(minutes)}, and a test event sent to check it did not arrive. Endpoint data still refreshes when
      this screen opens, but changes no longer reach OptimumDesk as they happen. Check Push in{' '}
      <a href="/app/settings/securitee/gravityzone" data-spa>
        Settings › Securitee › GravityZone
      </a>
      .
    </div>
  )
}

export function GravityZonePage() {
  const opened = useOpenRefresh()
  const [openedAt] = useState(() => Date.now())
  const [firstSync, setFirstSync] = useState<string | null | undefined>(undefined)
  const started = opened?.sync === 'started'
  const page = useQuery({ ...gzOverviewQuery(), refetchInterval: () => (started && Date.now() - openedAt < SYNC_WINDOW_MS ? SYNC_REFETCH_MS : false) })
  const currentSync = page.data?.links[0]?.synced_at ?? null
  useEffect(() => {
    if (firstSync === undefined && page.data) setFirstSync(currentSync)
  }, [firstSync, page.data, currentSync])
  /* Nota tine pana vin datele noi (alt synced_at) sau trece fereastra citirii. */
  const syncing = started && currentSync === firstSync && Date.now() - openedAt < SYNC_WINDOW_MS

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data

  return (
    <div id="content" className="gz-page">
      <header className="panel-heading">
        <h3>
          <strong>GravityZone</strong> protection
        </h3>
        <label className="color">endpoint protection read from Bitdefender GravityZone</label>
      </header>

      {opened?.push?.state === 'silent' && <PushStopped minutes={opened.push.silent_minutes ?? 0} />}
      {opened?.push?.state === 'checking' && <p className="gz-muted gz-refresh-note">Checking that GravityZone Push still works…</p>}
      {syncing && <p className="gz-muted gz-refresh-note">Reading the latest data from GravityZone…</p>}

      {!data.linked ? (
        <p className="gz-muted">
          This company is not linked to GravityZone yet. Link it in{' '}
          <a href="/app/settings/securitee/gravityzone" data-spa>
            Settings › Securitee › GravityZone
          </a>
          .
        </p>
      ) : data.waiting ? (
        <>
          <SyncLines data={data} />
          <p className="gz-muted">
            Waiting for the first sync from GravityZone. Start it now with <strong>Sync now</strong> in{' '}
            <a href="/app/settings/securitee/gravityzone" data-spa>
              Settings › Securitee › GravityZone
            </a>
            .
          </p>
        </>
      ) : (
        <>
          <SyncLines data={data} />
          <Cards data={data} />
          <Policies data={data} />
          <Gaps data={data} />
          <Endpoints data={data} />
        </>
      )}
    </div>
  )
}

function SyncLines({ data }: { data: GzOverview }) {
  return (
    <>
      {data.links.map((link) => (
        <div key={link.company_id}>
          <p className="gz-sync">
            <strong>{link.gz_company_name}</strong> in GravityZone · last sync {link.synced_at ?? 'not yet'}
            {link.endpoints !== null && (
              <>
                {' '}
                · {link.matched ?? 0} of {link.endpoints} GravityZone endpoints matched to OptimumDesk devices
              </>
            )}
          </p>
          {link.sync_error && <div className="alert alert-warning">Last sync failed: {link.sync_error}</div>}
        </div>
      ))}
    </>
  )
}

function Card({ title, value, note, bad = false }: { title: string; value: string; note: string; bad?: boolean }) {
  return (
    <div className={'gz-card' + (bad ? ' gz-card-bad' : '')}>
      <div className="gz-card-title">{title}</div>
      <div className="gz-card-value">{value}</div>
      <div className="gz-card-note">{note}</div>
    </div>
  )
}

function Cards({ data }: { data: GzOverview }) {
  const c = data.cards
  const issues = [
    ...c.modules_off.map((m) => `${m.label} off on ${m.count}`),
    ...(c.policy_not_applied > 0 ? [`policy not applied on ${c.policy_not_applied}`] : []),
    ...(c.signatures_outdated > 0 ? [`signatures outdated on ${c.signatures_outdated}`] : []),
  ]
  return (
    <div className="gz-cards">
      <Card title="Coverage" value={`${c.with_agent} / ${c.od_devices}`} note="OD devices with a GravityZone agent" />
      <Card title="Without antivirus" value={String(c.without_agent)} note="in OD, not protected by GravityZone" bad={c.without_agent > 0} />
      <Card title="Protection issues" value={String(c.issues)} note={issues.length > 0 ? issues.join(' · ') : 'essential modules on everywhere'} bad={c.issues > 0} />
      <Card title="Malware" value={String(c.malware)} note={c.malware === 1 ? 'active infection' : 'active infections'} bad={c.malware > 0} />
      <Card title="Not seen" value={String(c.not_seen)} note="GravityZone agent silent for 7+ days" />
      <div className="gz-card">
        <div className="gz-card-title">Hardening</div>
        <dl className="gz-card-pairs">
          <div>
            <dt className="gz-help" title={HARDENING_HELP.firewall_off}>
              Firewall off
            </dt>
            <dd>{c.hardening.firewall_off}</dd>
          </div>
          <div>
            <dt className="gz-help" title={HARDENING_HELP.power_user_on}>
              Power User on
            </dt>
            <dd>{c.hardening.power_user_on}</dd>
          </div>
        </dl>
        <div className="gz-card-note">of {c.hardening.of} GravityZone endpoints with an agent</div>
        {c.hardening.policies && c.hardening.policies.of > 0 ? (
          <div className="gz-card-note gz-card-policies">
            Policies: firewall off in {c.hardening.policies.firewall_off} of {c.hardening.policies.of} · Power User on in {c.hardening.policies.power_user_on} of{' '}
            {c.hardening.policies.of} · ransomware mitigation off in {c.hardening.policies.ransomware_off} of {c.hardening.policies.of}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/* "Match to device": statia GZ nepotrivita se leaga de mana de un aparat OD fara agent GZ. */
function MatchForm({ endpoint, devices, onDone, onCancel }: { endpoint: GzOverview['in_gz_not_od'][number]; devices: GzOverview['in_od_not_gz']; onDone: (next: GzOverview) => void; onCancel: () => void }) {
  const [device, setDevice] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    if (busy || device === '') return
    setBusy(true)
    setError('')
    try {
      onDone(await gzMatch(endpoint.gz_endpoint_id, Number(device)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The match could not be saved.')
      setBusy(false)
    }
  }

  return (
    <div className="gz-match-form">
      <select className="form-control input-sm" aria-label={`OptimumDesk device for ${endpoint.name}`} value={device} disabled={busy} onChange={(e) => setDevice(e.target.value)}>
        <option value="">Choose a device…</option>
        {devices.map((d) => (
          <option key={d.device_id} value={d.device_id}>
            {d.name}
          </option>
        ))}
      </select>
      <button type="button" className="btn btn-primary btn-xs" aria-label="Save match" disabled={busy || device === ''} onClick={() => void save()}>
        Save
      </button>
      <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      {error !== '' && <div className="text-danger gz-match-error">{error}</div>}
    </div>
  )
}

function Gaps({ data }: { data: GzOverview }) {
  const client = useQueryClient()
  const [matching, setMatching] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const canMatch = data.canMatch === true && data.in_od_not_gz.length > 0
  return (
    <div className="gz-gaps">
      <section aria-labelledby="gz-gap-od">
        <h4 id="gz-gap-od">In OD, without a GravityZone agent</h4>
        <p className="gz-gap-help">OptimumDesk devices without a GravityZone agent: no antivirus reported. Those GravityZone already knows need the agent installed from the GravityZone console.</p>
        {data.in_od_not_gz.length === 0 ? (
          <p className="gz-muted">None.</p>
        ) : (
          <ul className="gz-gap-list">
            {data.in_od_not_gz.map((d) => (
              <li key={d.device_id}>
                <a href={`/app/securitee/all_devices/${d.device_id}`} data-spa>
                  {d.name}
                </a>
                <span className="gz-muted">
                  {d.in_gz ? 'in GravityZone, no agent' : 'not in GravityZone'} · last activity {d.last_activity ?? 'unknown'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="gz-gap-gz">
        <h4 id="gz-gap-gz">In GravityZone, not in OD</h4>
        <p className="gz-gap-help">GravityZone endpoints that match no OptimumDesk device: install the OptimumDesk agent, or check the name.</p>
        {data.in_gz_not_od.length === 0 ? (
          <p className="gz-muted">None.</p>
        ) : (
          <ul className="gz-gap-list">
            {data.in_gz_not_od.map((e) => (
              <li key={e.gz_endpoint_id}>
                <span>{e.name}</span>
                <span className="gz-muted">{e.is_managed ? `no OD agent · seen in GZ ${e.last_seen_at ?? 'never'}` : 'no OD agent · no GZ agent either'}</span>
                {canMatch && matching !== e.gz_endpoint_id && (
                  <button type="button" className="btn btn-default btn-xs gz-match-button" aria-label={`Match ${e.name} to a device`} onClick={() => setMatching(e.gz_endpoint_id)}>
                    Match to device…
                  </button>
                )}
                {matching === e.gz_endpoint_id && (
                  <MatchForm
                    endpoint={e}
                    devices={data.in_od_not_gz}
                    onCancel={() => setMatching(null)}
                    onDone={(next) => {
                      client.setQueryData(gzOverviewQuery().queryKey, next)
                      setMatching(null)
                      setSaved(true)
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
        {saved && <p className="text-success gz-match-saved">Match to device saved.</p>}
      </section>
    </div>
  )
}

function Endpoints({ data }: { data: GzOverview }) {
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  const unmatch = async (gzEndpointId: string) => {
    if (busy) return
    setBusy(true)
    try {
      client.setQueryData(gzOverviewQuery().queryKey, await gzUnmatch(gzEndpointId))
    } finally {
      setBusy(false)
    }
  }
  return (
    <section aria-labelledby="gz-endpoints">
      <h4 id="gz-endpoints" className="gz-section-title">
        GravityZone endpoints ({data.endpoints.length})
      </h4>
      <div className="gz-table-wrap">
        <table className="table table-striped gz-endpoints">
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>GravityZone</th>
              <th>Policy</th>
              <th>Last seen in GZ</th>
              <th>Risk</th>
              <th>Hardening</th>
            </tr>
          </thead>
          <tbody>
            {data.endpoints.map((e) => (
              <tr key={e.gz_endpoint_id}>
                <td>
                  {e.device_id !== null ? (
                    <a href={`/app/securitee/all_devices/${e.device_id}`} data-spa>
                      {e.device_name ?? e.name}
                    </a>
                  ) : (
                    e.name
                  )}
                  {e.match_method === 'manual' && (
                    <>
                      {' '}
                      <span className="label label-default">manual</span>
                      {data.canMatch && (
                        <button type="button" className="btn btn-link btn-xs" aria-label={`Unmatch ${e.device_name ?? e.name}`} disabled={busy} onClick={() => void unmatch(e.gz_endpoint_id)}>
                          Unmatch
                        </button>
                      )}
                    </>
                  )}
                </td>
                <td>
                  <GzStatusLabel status={e} withReasons />
                </td>
                <td>{e.policy_name ?? '—'}</td>
                <td>{e.last_seen_at ?? '—'}</td>
                <td>{e.risk_score !== null ? `${e.risk_score}%` : '—'}</td>
                <td>{hardening(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/* "Power User on" se citea ca "e logat un power user" (Vasile, 26 oct). */
const HARDENING_HELP = {
  firewall_off: 'GravityZone firewall module off on the device. Often on purpose, when Windows Firewall is used instead.',
  power_user_on: 'GravityZone Power User module enabled: with its password, anyone at the device can change the protection settings locally.',
}

function hardening(e: GzEndpointRow) {
  const found = ([['firewall_off', 'Firewall off'], ['power_user_on', 'Power User on']] as const).filter(([key]) => e[key] === true)
  if (found.length === 0) return '—'
  return found.map(([key, text], i) => (
    <Fragment key={key}>
      {i > 0 && ' · '}
      <span className="gz-help" title={HARDENING_HELP[key]}>
        {text}
      </span>
    </Fragment>
  ))
}

/* Politicile GZ: auditul numara setarile pe politica ("firewall oprit in 7 din 11 politici"). */
function Policies({ data }: { data: GzOverview }) {
  const list = data.policies ?? []
  return (
    <section aria-labelledby="gz-policies" className="gz-policies">
      <h4 id="gz-policies" className="gz-section-title">
        GravityZone policies
      </h4>
      {list.length === 0 ? (
        <p className="gz-muted">Policies are read with the next sync (the GravityZone API key needs the Policies API).</p>
      ) : (
        <div className="gz-table-wrap">
          <table className="table table-condensed gz-policies-table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Endpoints</th>
                <th>Firewall</th>
                <th>Power User</th>
                <th>Ransomware mitigation</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.endpoints}</td>
                  <td>{setting(p.firewall, false)}</td>
                  <td>{setting(p.power_user, true)}</td>
                  <td>{setting(p.ransomware, false)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/** On / Off; portocaliu cand e slabiciune (firewall oprit, Power User pornit, ransomware oprit). */
function setting(value: boolean | null, weakWhen: boolean) {
  if (value === null) return '—'
  const text = value ? 'On' : 'Off'
  return value === weakWhen ? <span className="label label-warning">{text}</span> : text
}
